import React, { useState } from 'react';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { ProductApi } from 'common/types';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import * as _ from 'lodash';
import { useTranslation } from 'services/localization/useTranslation';
import * as StockSelectors from 'selectors/StockSelectors';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';

interface Props {
	open: boolean;
	onSave: (products: string[]) => void;
	onCancel: () => void;
	products: ProductApi[];
	activeProducts: string[];
	classes?: any;
	disabledProducts?: string[];
}

const ProductDialog = (props: Props) => {
	const categories = useSelector(StockSelectors.categoriesData);
	const [activeProducts, setActiveProducts] = useState<string[]>([...props.activeProducts]);

	const productsByCategory = props.products.reduce((map, product) => {
		product.categoryIds?.forEach((categoryId) => {
			map[categoryId] = map[categoryId] ? [...map[categoryId], product] : [product];
		});
		return map;
	}, {} as { [key: string]: ProductApi[] });

	const productsWithoutCategory = props.products.filter((p) => !p.categoryIds?.length);

	const { t } = useTranslation();

	const handleCancel = () => {
		props.onCancel();
	};

	const handleOk = () => {
		props.onSave(activeProducts);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>, productId: string) => {
		const checked = event.target.checked;
		if (checked) {
			setActiveProducts([...activeProducts, productId]);
		} else {
			setActiveProducts(activeProducts.filter((id) => id !== productId));
		}
	};

	const isActive = (productId: string): boolean => {
		return !!_.find(activeProducts, (id) => id === productId);
	};

	return (
		<Dialog
			disableEscapeKeyDown
			maxWidth="sm"
			aria-labelledby="confirmation-dialog-title"
			open={props.open}
			classes={props.classes}
			fullWidth
		>
			<DialogTitle id="confirmation-dialog-title">
				{t('product.chooseProducts', 'Choose products')}
			</DialogTitle>
			<DialogContent>
				<FormControl>
					<FormGroup>
						{categories.map((category) => {
							const products = productsByCategory[category.id] ?? [];
							if (products.length === 0) return null;
							return (
								<Box mt={4} key={category.id}>
									<Typography variant="body2" color="textSecondary" gutterBottom>
										{category.name.def}
									</Typography>
									<Box>
										{products.map((product, index) => {
											const isDisabled = props.disabledProducts?.includes(product.id);
											return (
												<div>
													<FormControlLabel
														key={product.id + index}
														control={
															<Checkbox
																color="primary"
																checked={isActive(product.id)}
																onChange={(e) => handleChange(e, product.id)}
																value={product.name.def}
																disabled={isDisabled}
															/>
														}
														label={product.name.def}
													/>
												</div>
											);
										})}
									</Box>
								</Box>
							);
						})}
						{productsWithoutCategory.length > 0 && (
							<Box mt={4}>
								<Typography variant="body2" color="textSecondary" gutterBottom>
									{t('ProductCategories.noCategory')}
								</Typography>
								<Box>
									{productsWithoutCategory.map((product, index) => {
										const isDisabled = props.disabledProducts?.includes(product.id);
										return (
											<div>
												<FormControlLabel
													key={product.id + index}
													control={
														<Checkbox
															color="primary"
															checked={isActive(product.id)}
															onChange={(e) => handleChange(e, product.id)}
															value={product.name.def}
															disabled={isDisabled}
														/>
													}
													label={product.name.def}
												/>
											</div>
										);
									})}
								</Box>
							</Box>
						)}
					</FormGroup>
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel} color="secondary">
					{t('common:actions.cancel')}
				</Button>
				<Button onClick={handleOk} color="primary" variant="contained">
					{t('common:greetings.okBtn')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ProductDialog;
